import React from 'react';
import isNil from 'utils/isNil';
import SEO, { createSEO } from 'components/SEO';
import useDynamicHeroImages from 'hooks/useDynamicHeroImages';
import TextbasedSocialProof from 'components/TextbasedSocialProof';
import DynamicHero from 'components/Hero/DynamicHero';
import FeatureFaq from 'components/FeatureFaq';
import ContentBlock from 'components/ContentBlock';
import ConversionPanel from 'components/ConversionPanel';
import TwoColumnLayout from 'components/TwoColumnLayout';
import Toggle from 'components/Toggle';
import CardBlock from 'components/CardBlock';
import WebsiteTrustbar from 'components/WebsiteTrustbar';
import EmbedCode from 'components/EmbedCode';
import RowContent from 'components/RowContent';
import Table from 'components/Table';
import InPageFeaturesV3 from 'components/InPageFeatures/InPageFeaturesV3';
import InPageFeaturesV2 from 'components/InPageFeatures/InPageFeaturesV2';
import InPageFeaturesV1 from 'components/InPageFeatures/InPageFeaturesV1';
import ToolSection from 'components/Tools/ToolSection';
import colorTypeHelper from 'utils/colorTypeHelper';
import generateBreadcrumbs from 'utils/getBreadcrumbData';
import { SectionLayout } from 'components/Sections/SectionLayout';
import RowWrapper from 'components/RowWrapper';
import Section from 'components/Section';

const buildPageComponents = props => {
  const { seo, components } = props?.data?.contentfulWebsitePage;
  const { pageUrl } = seo || {};
  const dataImages = useDynamicHeroImages();
  const faq = props?.data?.contentfulWebsitePage?.components?.find(
    el => el.__typename === 'ContentfulWebsiteAccordion'
  );
  const breadcrumbs = generateBreadcrumbs(seo, null);
  const pageSeo = createSEO(seo, props, faq, breadcrumbs);

  return (
    <>
      <SEO {...pageSeo} />
      {components?.map((ele, index) => {
        const type = ele.__typename;

        if (type === 'ContentfulDynamicHero') {
          return <DynamicHero data={ele} images={dataImages} seo={seo} key={index} />;
        }
        if (type === 'ContentfulRow') {
          return <RowWrapper data={ele} key={index} />;
        }
        if (type === 'ContentfulSection') {
          return <Section data={ele} key={index} />;
        }
        if (type === 'ContentfulRowContent') {
          return (
            <RowContent
              anchorId={ele.anchorId}
              bgColor={ele.bgColor}
              bgCornerImg={ele.backgroundCornerImage}
              bgImg={ele.backgroundImage?.image?.fluid?.src}
              bgFlip={ele.flipBackgroundImage}
              gradientDirection={ele.gradientDirection}
              columns={ele.columns}
              fullWidth={ele.fullWidthContent}
              key={index}
            />
          );
        }
        if (type === 'Contentful2ColumnLayout') {
          return (
            <TwoColumnLayout
              anchorId={ele.anchorId}
              leftAlignContent={ele.leftAlignContent}
              columnOne={ele.columnOne}
              columnTwo={ele.columnTwo}
              backgroundColor={ele.backgroundColor}
              cornerImage={ele.cornerImage}
              pageType={['terms-of-use', 'privacy-policy'].includes(pageUrl) ? 'terms' : 'normal'}
              slug={pageUrl}
              key={index}
            />
          );
        }
        if (type === 'ContentfulConversionPanel') {
          return (
            <ConversionPanel
              style={ele.style}
              headline={ele.headline}
              subheading={ele.subheading}
              colorScheme={ele.colorScheme}
              buttonLabel={ele.callToAction ? ele.callToAction[0].buttonLabel : ''}
              utm={ele.callToAction?.length > 0 && ele.callToAction[0]?.carryUtmParameters}
              url={ele.callToAction && ele.callToAction.length > 0 ? ele.callToAction[0].url : ''}
              useModalForm={ele.callToAction?.length > 0 && ele.callToAction[0]?.useModalForm}
              modalForm={ele.callToAction?.length > 0 && ele.callToAction[0]?.modalForm}
              key={index}
            />
          );
        }
        if (type === 'ContentfulWebsiteContentBlock' && ele?.collection?.length > 0) {
          return <ContentBlock data={ele} key={index} />;
        }
        if (type === 'ContentfulTextBasedSocialProof') {
          return (
            <TextbasedSocialProof
              background={ele.bgColor}
              title={ele.title}
              description={!isNil(ele.description) ? ele.description.description : ''}
              ratingScore={ele.stars}
              logoImgs={ele.companyLogos}
              key={index}
            />
          );
        }
        if (type === 'ContentfulInPageFeatures') {
          if (ele.useV2Styling) {
            return (
              <InPageFeaturesV2
                background={ele.bg}
                title={ele.title}
                description={!isNil(ele.description) ? ele.description.description : ''}
                inPageNavigation={ele.inPageNavigationItems}
                backgroundColor={colorTypeHelper[ele.bgColor]}
                key={index}
              />
            );
          }
          if (ele.useV3Styling) {
            return <InPageFeaturesV3 {...ele} index={index} key={index} />;
          }
          return (
            <InPageFeaturesV1
              background={ele.bg}
              title={ele.title}
              description={!isNil(ele.description) ? ele.description.description : ''}
              inPageNavigation={ele.inPageNavigationItems}
              backgroundColor={colorTypeHelper[ele.bgColor]}
              key={index}
            />
          );
        }
        if (type === 'ContentfulWebsiteAccordion') {
          return (
            <FeatureFaq
              background={ele.backgroundColor?.backgroundColor}
              title={ele.heading}
              subhead={ele.subhead && ele.subhead.subhead}
              faqs={ele.components}
              key={index}
            />
          );
        }
        if (type === 'ContentfulCardsBlock' && ele.cardType) {
          return <CardBlock data={ele} key={index} />;
        }
        if (type === 'ContentfulWebsiteTrustbar') {
          return (
            <WebsiteTrustbar
              heading={ele.heading}
              backgroundColor={ele.trustbarBGColor}
              companies={ele.companies}
            />
          );
        }
        if (type === 'ContentfulEmbedCode') {
          return <EmbedCode code={ele.embedCode?.json} headCode={ele.headCode?.json} key={index} />;
        }
        if (type === 'ContentfulToggle') {
          return <Toggle {...ele} key={index} />;
        }
        if (type === 'ContentfulTool') {
          return <ToolSection {...ele} key={index} />;
        }
        if (type === 'ContentfulTable') {
          return (
            <SectionLayout key={index}>
              <Table {...ele} />
            </SectionLayout>
          );
        }

        return null;
      })}
    </>
  );
};

export default buildPageComponents;
